'use client';

import { useFocusRing } from '@react-aria/focus';
import { useHover } from '@react-aria/interactions';
import { mergeRefs } from '@react-aria/utils';
import { type ElementType, type ReactNode, type RefObject } from 'react';

import { type VariantProps } from 'tailwind-variants';

import { focusRingStyles } from '../../../styles/focusRing';
import { type PolymorphicComponentProps } from '../../../types/component';
import { mergeProps } from '../../../utils/mergeProps';
import { tv } from '../../../utils/tv';
import {
  Typography,
  type TypographyBaseProps,
} from '../../typography/Typography';

type LinkProps<T extends ElementType = 'a'> = PolymorphicComponentProps<
  LinkBaseProps,
  T
>;

const linkClasses = {
  root: 'zds-link-root',
} as const;

const linkStyles = tv({
  slots: {
    root: [
      linkClasses.root,
      'cursor-pointer',
      'border-0',
      'bg-transparent',
      'p-0',
      'appearance-none',
      '[text-align:inherit]',
    ],
  },
  variants: {
    underline: {
      always: {
        root: 'underline',
      },
      hovered: {
        root: 'no-underline hover:underline focus-visible:underline',
      },
    },
    color: {
      'accent-primary': {
        root: [
          'hover:text-accent-primary-main-hovered',
          'visited:z--link-accent-primary-color-visited',
          'visited:hover:z--link-accent-primary-color-visited-hovered',
        ],
      },
      'static-white': {
        root: [
          'hover:text-static-white-main-hovered',
          'visited:z--link-static-white-color-visited',
          'visited:hover:z--link-static-white-color-visited-hovered',
        ],
      },
      'text-primary': {
        root: [
          'hover:z--link-text-primary-color-hovered',
          'visited:z--link-text-primary-color-visited',
          'visited:hover:z--link-text-primary-color-visited-hovered',
        ],
      },
    },
    isDisabled: {
      true: {
        root: [
          'cursor-not-allowed disabled-foreground',
          'hover:disabled-foreground',
          'visited:disabled-foreground',
          'visited:hover:disabled-foreground',
        ],
      },
    },
  },
  compoundVariants: [
    {
      underline: 'hovered',
      isDisabled: true,
      class: { root: 'hover:no-underline focus-visible:no-underline' },
    },
  ],
});

type Variant = VariantProps<typeof linkStyles>;

interface LinkBaseProps
  extends Omit<TypographyBaseProps, 'color' | 'align' | 'noWrap'> {
  /**
   * The Link's underline behaviour.
   *
   * @default 'always'
   */
  underline?: Variant['underline'];
  /**
   * The Link's text colour.
   *
   * @default 'accent-primary'
   */
  color?: Variant['color'];
  /**
   * If `true`, the Link is disabled, removed from the tab order, and
   * announced as disabled to assistive technology.
   *
   * @default false
   */
  isDisabled?: boolean;
  /**
   * Visually-hidden text appended to the Link's accessible name when
   * `target="_blank"`.
   */
  newTabAnnouncement?: ReactNode;
}

// Presentational props threaded from the hook branches to `LinkBase`. Branches
// spread `localProps` (styling/content) plus their computed element attrs
// directly; `LinkBase` destructures its known props and forwards `...rest`.
type LinkBaseInternalProps = LinkProps & {
  Root: ElementType;
  internalRef: RefObject<HTMLElement | null>;
  isPressed: boolean;
};

// Shared, element-agnostic render. Defines the `<Typography>` markup + styles
// once so both interaction branches produce identical output, differing only in
// the element attrs and `isPressed` they pass in.
const LinkBase = (props: LinkBaseInternalProps) => {
  const {
    Root,
    internalRef,
    isPressed,
    underline,
    color,
    variant,
    className,
    ref,
    isDisabled,
    newTabAnnouncement,
    children,
    ...rest
  } = props;
  const { isFocusVisible, isFocused, focusProps } = useFocusRing();
  const { hoverProps, isHovered } = useHover({ isDisabled });
  const styles = linkStyles({ underline, color, isDisabled });
  const focusStyles = focusRingStyles();

  return (
    <Typography
      // Merge branch interaction props with focus/hover handlers. Cast because
      // the polymorphic generic erases the element prop type inside the body.
      {...(mergeProps(rest, focusProps, hoverProps) as LinkProps)}
      component={Root}
      variant={variant}
      color={color}
      className={styles.root({ className: [focusStyles.root(), className] })}
      ref={mergeRefs(internalRef, ref)}
      data-disabled={isDisabled || undefined}
      data-focused={isFocused || undefined}
      data-focus-visible={isFocusVisible || undefined}
      data-hovered={isHovered || undefined}
      data-pressed={isPressed || undefined}
    >
      {children}
      {props.target === '_blank' && !isDisabled && newTabAnnouncement ? (
        <span className="sr-only">{newTabAnnouncement}</span>
      ) : null}
    </Typography>
  );
};

LinkBase.displayName = 'LinkBase';

export { LinkBase, linkClasses };
export type { LinkBaseProps, LinkProps };
